<script setup lang="ts">
/**
 * Die Landing: eine Seite, prerendert. Hier hängen alle Suchmaschinen- und
 * Social-Metadaten (Title/Description, Canonical, Open Graph, Twitter Card)
 * sowie die strukturierten Daten (schema.org: Organization → WebSite →
 * SoftwareApplication mit dem kostenlosen Starter-Angebot). Keine erfundenen
 * Bewertungen oder Zahlen — nur, was die Seite auch zeigt.
 *
 * Sections unterhalb der Story hydrieren erst kurz vor dem Sichtbarwerden
 * (600px Vorlauf): weniger Hauptthread-Arbeit beim Start, die Reveal-
 * Animationen setzen trotzdem an, bevor die Section ins Bild kommt. Hero und
 * Story hydrieren sofort — die Story misst ihre Bühne beim Mount.
 */
const { public: { siteUrl } } = useRuntimeConfig()

const title = 'Clarify | The ticket bot for Discord'
const description
  = 'Discord support, sorted. Tickets from a panel or DM become tagged forum threads, Clarify relays every reply, and payment data is already on the ticket.'
const ogImage = `${siteUrl}/og.png`
const canonical = `${siteUrl}/`

useSeoMeta({
  title,
  description,
  robots: 'index, follow',
  ogType: 'website',
  ogSiteName: 'Clarify',
  ogLocale: 'en_US',
  ogUrl: canonical,
  ogTitle: title,
  ogDescription: description,
  ogImage,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: 'Clarify — no more lost tickets. The ticket bot for Discord.',
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: ogImage,
  twitterImageAlt: 'Clarify — no more lost tickets. The ticket bot for Discord.'
})

const organizationId = `${siteUrl}/#organization`
const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': organizationId,
      'name': 'Clarify',
      'url': canonical,
      'logo': `${siteUrl}/brand/clarify-mark-512.png`
    },
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      'url': canonical,
      'name': 'Clarify',
      'inLanguage': 'en',
      'publisher': { '@id': organizationId }
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${siteUrl}/#software`,
      'name': 'Clarify',
      'url': canonical,
      'description': description,
      'image': ogImage,
      'applicationCategory': 'BusinessApplication',
      'applicationSubCategory': 'Ticketing for Discord communities',
      'operatingSystem': 'Discord',
      'author': { '@id': organizationId },
      'publisher': { '@id': organizationId },
      'offers': {
        '@type': 'Offer',
        'name': 'Starter',
        'price': '0',
        'priceCurrency': 'EUR',
        'url': `${canonical}#plans`,
        'availability': 'https://schema.org/InStock'
      }
    }
  ]
}

useHead({
  link: [{ rel: 'canonical', href: canonical }],
  script: [
    {
      key: 'ld-json',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(structuredData)
    }
  ]
})

/* 600px Vorlauf: hydrieren, bevor die Section ins Bild scrollt */
const hydrateAhead = { rootMargin: '600px' }
</script>

<template>
  <main id="main">
    <HeroSection />
    <HowItWorksSection />
    <LazyVisibleModulesSection :hydrate-on-visible="hydrateAhead" />
    <LazyVisibleIntegrationsSection :hydrate-on-visible="hydrateAhead" />
    <LazyVisibleConnectorsSection :hydrate-on-visible="hydrateAhead" />
    <!--
      TODO(legal): Testimonials section removed — the previous quotes were placeholders, not real
      customer statements (UWG § 5 / irreführende Werbung). Re-add <LazyTestimonialsSection /> here
      once real, written-approved testimonials (full name/company or explicit consent) exist,
      and update the data in app/components/sections/TestimonialsSection.vue.
    -->
    <LazyVisiblePricingSection :hydrate-on-visible="hydrateAhead" />
  </main>
</template>
